/**
 * Taking a workspace's applications out, and bringing them back in.
 *
 * Two halves of one idea and one endpoint pair each: an export produces a
 * signed archive, and an import reads one back into a workspace. Both are jobs,
 * because both walk every row of every table.
 *
 * Nothing here is React and nothing here fetches. What it holds is the two
 * decisions the forms make that are worth being sure about: what an empty
 * selection means, and when an export has something to download.
 */
import type { Job } from '@/api/types'

/**
 * What to send for a selection of applications.
 *
 * **An empty list means everything**, which is the endpoint's own rule -- and
 * the reason the list is omitted rather than sent empty when everything is
 * chosen: the two are the same request, and building it from what is ticked
 * would send a hundred ids to say "all of them".
 *
 * It is also the difference between "everything, including whatever is added
 * before the job runs" and "these ninety-nine". Choosing all of them means the
 * first.
 */
export function exportBody(
  chosen: readonly number[],
  all: readonly number[],
  onlyStructure: boolean,
): { application_ids?: number[]; only_structure: boolean } {
  const everything = chosen.length === all.length
  return {
    ...(everything ? {} : { application_ids: [...chosen] }),
    only_structure: onlyStructure,
  }
}

/**
 * An export's file, once there is one.
 *
 * Decided on the url alone, and not also on the state. The job's resource is
 * attached when the export succeeds and at no other moment, so a running,
 * failed or cancelled job has no url -- the two checks are the same check, and
 * no test could tell them apart. The url is also the stronger of the two: a job
 * that finished and whose archive has since been cleared away is finished and
 * has nothing to offer.
 */
export function exportFile(job: {
  url?: string | null
  exported_file_name?: string | null
}): { url: string; name: string } | null {
  if (job.url == null || job.url === '') return null
  return { url: job.url, name: job.exported_file_name ?? '' }
}

/**
 * Whether an export is still being made.
 *
 * Read from the state rather than from the absence of a file: a failed export
 * has no file either, and calling that "still running" would leave a spinner
 * turning forever over something that has already gone wrong.
 */
export function isExportRunning(job: Pick<Job, 'state'>): boolean {
  return job.state !== 'finished' && job.state !== 'failed' && job.state !== 'cancelled'
}
